import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Edit, Trash2, DollarSign, AlertTriangle, Eye, Calendar, User, CreditCard, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { usePropiedad } from '../contexts/PropiedadContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Ingresos = () => {
  const { propiedadSeleccionada } = usePropiedad();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const anioParam = searchParams.get('anio');
  
  const [ingresos, setIngresos] = useState([]);
  const [canales, setCanales] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  const [estadosIngreso, setEstadosIngreso] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [ingresoEditando, setIngresoEditando] = useState(null);
  const [ingresoAEliminar, setIngresoAEliminar] = useState(null);
  const [modalReservaAbierto, setModalReservaAbierto] = useState(false);
  const [reservaPreview, setReservaPreview] = useState(null);
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroCanal, setFiltroCanal] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroAnio, setFiltroAnio] = useState(anioParam || new Date().getFullYear().toString());
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [ordenColumna, setOrdenColumna] = useState('fecha_cobro');
  const [ordenDireccion, setOrdenDireccion] = useState('desc'); // 'asc' o 'desc'
  
  const [ingresosRecurrentes, setIngresosRecurrentes] = useState([]);
  const [modalRecurrenteAbierto, setModalRecurrenteAbierto] = useState(false);
  const [modalVerRecurrentesAbierto, setModalVerRecurrentesAbierto] = useState(false);
  const [modalDetalleRecurrente, setModalDetalleRecurrente] = useState(false);
  const [recurrenteDetalle, setRecurrenteDetalle] = useState(null);
  const [ingresosDelRecurrente, setIngresosDelRecurrente] = useState([]);
  const [resumenRecurrente, setResumenRecurrente] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [recurrenteEditando, setRecurrenteEditando] = useState(null);
  const [recurrenteAEliminar, setRecurrenteAEliminar] = useState(null);
  const [eliminarPendientes, setEliminarPendientes] = useState(true);
  const [filtroCanalRecurrente, setFiltroCanalRecurrente] = useState('');
  
  const [formRecurrente, setFormRecurrente] = useState({
    canal_id: '',
    dia_cobro: 1,
    monto_bruto: 0,
    comisiones: 0,
    frecuencia_meses: 1,
    repeticiones: 12,
    metodo_pago: 'Transferencia',
    fecha_inicio: '',
    notas: ''
  });

  const [formData, setFormData] = useState({
    fecha_cobro: '',
    propiedad_id: '',
    canal_id: '',
    monto_bruto: 0,
    comisiones: 0,
    metodo_pago: '',
    estado: 'PENDIENTE',
    notas: ''
  });

  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatos();
    }
  }, [propiedadSeleccionada]);

  const cargarDatos = async () => {
    if (!propiedadSeleccionada) return;
    
    try {
      const [resIngresos, resIngresosRecurrentes, resCanales, resReservas, resMediosPago, resEstadosIngreso] = await Promise.all([
        axios.get(`${API}/ingresos?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/ingresos-recurrentes?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/canales`),
        axios.get(`${API}/reservas?propiedad_id=${propiedadSeleccionada.id_propiedad}`),
        axios.get(`${API}/medios-pago`),
        axios.get(`${API}/estados-ingreso`)
      ]);
      setIngresos(resIngresos.data);
      setIngresosRecurrentes(resIngresosRecurrentes.data);
      setCanales(resCanales.data);
      setReservas(resReservas.data);
      setMediosPago(resMediosPago.data.filter(m => m.activo !== false));
      setEstadosIngreso(resEstadosIngreso.data);
      
      // Establecer propiedad seleccionada por defecto en el formulario
      setFormData(prev => ({ 
        ...prev, 
        propiedad_id: propiedadSeleccionada.id_propiedad 
      }));
      
      if (resCanales.data.length > 0) {
        setFormData(prev => ({ ...prev, canal_id: resCanales.data[0].id_canal }));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  // Obtener reserva por ID
  const obtenerReserva = (reservaId) => {
    return reservas.find(r => r.id_reserva === reservaId);
  };

  // Abrir modal de vista previa de reserva
  const abrirVistaReserva = (reservaId) => {
    const reserva = obtenerReserva(reservaId);
    if (reserva) {
      setReservaPreview(reserva);
      setModalReservaAbierto(true);
    }
  };

  const abrirModal = (ingreso = null) => {
    if (ingreso) {
      setIngresoEditando(ingreso);
      setFormData({
        fecha_cobro: ingreso.fecha_cobro,
        propiedad_id: ingreso.propiedad_id,
        canal_id: ingreso.canal_id,
        monto_bruto: ingreso.monto_bruto,
        comisiones: ingreso.comisiones,
        metodo_pago: ingreso.metodo_pago,
        estado: ingreso.estado || estadosIngreso[0]?.nombre || 'Pendiente',
        notas: ingreso.notas || ''
      });
    } else {
      // Al crear nuevo, asignar automáticamente la propiedad seleccionada
      setFormData({
        fecha_cobro: '',
        propiedad_id: propiedadSeleccionada?.id_propiedad || '',
        canal_id: canales[0]?.id_canal || '',
        monto_bruto: 0,
        comisiones: 0,
        metodo_pago: 'Transferencia',
        estado: estadosIngreso[0]?.nombre || 'Pendiente',
        notas: ''
      });
      setIngresoEditando(null);
    }
    setModalAbierto(true);
  };

  const guardarIngreso = async () => {
    try {
      if (ingresoEditando) {
        await axios.put(`${API}/ingresos/${ingresoEditando.id_ingreso}`, formData);
        toast.success('Ingreso actualizado');
      } else {
        await axios.post(`${API}/ingresos`, formData);
        toast.success('Ingreso creado');
      }
      cargarDatos();
      setModalAbierto(false);
    } catch (error) {
      toast.error('Error al guardar ingreso');
    }
  };

  
  
  const verDetalleRecurrente = async (ir) => {
    setCargandoDetalle(true);
    setRecurrenteDetalle(ir);
    setModalVerRecurrentesAbierto(false);
    setModalDetalleRecurrente(true);
    try {
      const res = await axios.get(`${API}/ingresos-recurrentes/${ir.id_ingreso_recurrente}/ingresos`);
      let ingresos = res.data.ingresos || res.data || [];
      // Ordenar cronológicamente (ascendente)
      ingresos.sort((a, b) => {
        if (!a.fecha_cobro) return 1;
        if (!b.fecha_cobro) return -1;
        return a.fecha_cobro.localeCompare(b.fecha_cobro);
      });
      setIngresosDelRecurrente(ingresos);
      setResumenRecurrente(res.data.resumen || null);
    } catch (error) {
      toast.error('Error al cargar detalle');
      setIngresosDelRecurrente([]);
      setResumenRecurrente(null);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const vincularIngresosExistentes = async () => {
    if (!recurrenteDetalle) return;
    try {
      const res = await axios.post(`${API}/ingresos-recurrentes/${recurrenteDetalle.id_ingreso_recurrente}/vincular`);
      toast.success(res.data.message || 'Ingresos vinculados exitosamente');
      verDetalleRecurrente(recurrenteDetalle);
      cargarDatos();
    } catch (error) {
      toast.error('Error al vincular ingresos');
    }
  };

  const cambiarEstadoIngresoDetalle = async (ingreso) => {
    try {
      const nuevoEstado = ingreso.estado.toUpperCase() === 'PAGADO' ? 'PENDIENTE' : 'Pagado';
      await axios.patch(`${API}/ingresos/${ingreso.id_ingreso}/estado?estado=${nuevoEstado}`);
      toast.success(`Estado cambiado a ${nuevoEstado}`);
      verDetalleRecurrente(recurrenteDetalle);
      cargarDatos();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const eliminarIngresoDetalle = async (ingreso) => {
    if (!window.confirm('¿Seguro que deseas eliminar este ingreso?')) return;
    try {
      await axios.delete(`${API}/ingresos/${ingreso.id_ingreso}`);
      toast.success('Ingreso eliminado');
      verDetalleRecurrente(recurrenteDetalle);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar ingreso');
    }
  };


const abrirModalRecurrente = (recurrente = null) => {
    if (recurrente) {
      setRecurrenteEditando(recurrente);
      setFormRecurrente({
        canal_id: recurrente.canal_id,
        dia_cobro: recurrente.dia_cobro,
        monto_bruto: recurrente.monto_bruto,
        comisiones: recurrente.comisiones,
        frecuencia_meses: recurrente.frecuencia_meses,
        repeticiones: recurrente.repeticiones,
        metodo_pago: recurrente.metodo_pago || 'Transferencia',
        fecha_inicio: recurrente.fecha_inicio || '',
        notas: recurrente.notas || ''
      });
    } else {
      setRecurrenteEditando(null);
      setFormRecurrente({
        canal_id: canales[0]?.id_canal || '',
        dia_cobro: 1,
        monto_bruto: 0,
        comisiones: 0,
        frecuencia_meses: 1,
        repeticiones: 12,
        metodo_pago: 'Transferencia',
        fecha_inicio: new Date().toISOString().split('T')[0],
        notas: ''
      });
    }
    setModalRecurrenteAbierto(true);
  };

  const guardarIngresoRecurrente = async () => {
    try {
      const dataToSave = {
        ...formRecurrente,
        propiedad_id: propiedadSeleccionada.id_propiedad
      };

      if (recurrenteEditando) {
        await axios.put(`${API}/ingresos-recurrentes/${recurrenteEditando.id_ingreso_recurrente}`, dataToSave);
        toast.success('Ingreso recurrente actualizado');
      } else {
        await axios.post(`${API}/ingresos-recurrentes`, dataToSave);
        toast.success('Ingreso recurrente creado');
      }
      cargarDatos();
      setModalRecurrenteAbierto(false);
    } catch (error) {
      toast.error('Error al guardar ingreso recurrente');
    }
  };

  const eliminarIngresoRecurrente = async () => {
    if (!recurrenteAEliminar) return;
    try {
      await axios.delete(`${API}/ingresos-recurrentes/${recurrenteAEliminar.id_ingreso_recurrente}?eliminar_pendientes=${eliminarPendientes}`);
      toast.success('Ingreso recurrente eliminado');
      setRecurrenteAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar ingreso recurrente');
    }
  };

  const cerrarModalRecurrente = () => {
    setModalRecurrenteAbierto(false);
    setRecurrenteEditando(null);
  };

  const confirmarEliminacion = (ingreso) => {
    setIngresoAEliminar(ingreso);
  };

  const eliminarIngreso = async () => {
    if (!ingresoAEliminar) return;
    
    try {
      await axios.delete(`${API}/ingresos/${ingresoAEliminar.id_ingreso}`);
      toast.success('Ingreso eliminado exitosamente');
      setIngresoAEliminar(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al eliminar el ingreso');
      console.error('Error:', error);
    }
  };

  const cambiarEstadoIngreso = async (idIngreso, nuevoEstado) => {
    try {
      await axios.patch(`${API}/ingresos/${idIngreso}/estado?estado=${encodeURIComponent(nuevoEstado)}`);
      setIngresos(prev => prev.map(ing => 
        ing.id_ingreso === idIngreso ? { ...ing, estado: nuevoEstado } : ing
      ));
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar estado');
      console.error('Error:', error);
    }
  };

  const obtenerEstadoConfig = (nombreEstado) => {
    return estadosIngreso.find(e => e.nombre.toLowerCase() === nombreEstado?.toLowerCase());
  };

  const formatCurrency = (value) => {
    // Formatear con separador de miles con punto
    const formatted = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `$${formatted}`;
  };

  // Obtener años únicos de los ingresos
  const aniosDisponibles = [...new Set(ingresos.map(i => new Date(i.fecha_cobro + 'T12:00:00').getFullYear()))].sort((a, b) => b - a);

  // Función para obtener canal completo (nombre y color)
  const obtenerCanal = (canalId) => {
    return canales.find(c => c.id_canal === canalId);
  };

  const ingresosFiltrados = ingresos.filter(ingreso => {
    const fechaIngreso = new Date(ingreso.fecha_cobro + 'T12:00:00');

    // Filtro por rango de fechas (tiene prioridad sobre año y mes)
    if (filtroFechaDesde || filtroFechaHasta) {
      if (filtroFechaDesde) {
        const desde = new Date(filtroFechaDesde + 'T00:00:00');
        if (fechaIngreso < desde) return false;
      }
      if (filtroFechaHasta) {
        const hasta = new Date(filtroFechaHasta + 'T23:59:59');
        if (fechaIngreso > hasta) return false;
      }
    } else {
      // Filtro por año (solo si no hay rango de fechas)
      if (filtroAnio && fechaIngreso.getFullYear() !== parseInt(filtroAnio)) {
        return false;
      }

      // Filtro por mes (solo si no hay rango de fechas)
      if (filtroMes) {
        const [year, month] = filtroMes.split('-');
        if (fechaIngreso.getFullYear() !== parseInt(year) || 
            (fechaIngreso.getMonth() + 1) !== parseInt(month)) {
          return false;
        }
      }
    }

    // Filtro por canal
    if (filtroCanal && ingreso.canal_id !== filtroCanal) {
      return false;
    }

    // Filtro por estado
    if (filtroEstado && ingreso.estado !== filtroEstado) {
      return false;
    }

    return true;
  });

  const limpiarFiltros = () => {
    setFiltroMes('');
    setFiltroCanal('');
    setFiltroEstado('');
    setFiltroAnio('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
  };

  // Calcular total de monto neto filtrado
  const totalMontoNetoFiltrado = ingresosFiltrados.reduce((sum, ingreso) => sum + (ingreso.monto_neto || 0), 0);

  // Función para manejar el ordenamiento
  const handleOrdenar = (columna) => {
    if (ordenColumna === columna) {
      setOrdenDireccion(ordenDireccion === 'asc' ? 'desc' : 'asc');
    } else {
      setOrdenColumna(columna);
      setOrdenDireccion('asc');
    }
  };

  // Función para obtener el valor de ordenamiento de una columna
  const getValorOrdenamiento = (ingreso, columna) => {
    switch (columna) {
      case 'fecha_cobro':
        return new Date(ingreso.fecha_cobro + 'T12:00:00').getTime();
      case 'canal':
        const canal = obtenerCanal(ingreso.canal_id);
        return canal?.nombre?.toLowerCase() || '';
      case 'reserva':
        const reserva = ingreso.reserva_id ? obtenerReserva(ingreso.reserva_id) : null;
        return reserva?.huesped_nombre?.toLowerCase() || '';
      case 'estado':
        return ingreso.estado?.toLowerCase() || '';
      case 'metodo_pago':
        return ingreso.metodo_pago?.toLowerCase() || '';
      case 'comisiones':
        return ingreso.comisiones || 0;
      case 'monto_neto':
        return ingreso.monto_neto || 0;
      default:
        return '';
    }
  };

  // Ingresos filtrados y ordenados
  const ingresosOrdenados = useMemo(() => {
    return [...ingresosFiltrados].sort((a, b) => {
      const valorA = getValorOrdenamiento(a, ordenColumna);
      const valorB = getValorOrdenamiento(b, ordenColumna);
      
      if (valorA < valorB) return ordenDireccion === 'asc' ? -1 : 1;
      if (valorA > valorB) return ordenDireccion === 'asc' ? 1 : -1;
      return 0;
    });
  }, [ingresosFiltrados, ordenColumna, ordenDireccion]);

  // Componente para header ordenable
  const HeaderOrdenable = ({ columna, children, className = '' }) => (
    <th 
      className={`text-left p-3 text-[#0E3A53] font-semibold cursor-pointer hover:bg-[#8FE0FF]/20 transition-colors select-none ${className}`}
      onClick={() => handleOrdenar(columna)}
    >
      <div className="flex items-center gap-1">
        {children}
        {ordenColumna === columna ? (
          ordenDireccion === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-[#1AA1D8]" />
          ) : (
            <ArrowDown className="w-4 h-4 text-[#1AA1D8]" />
          )
        ) : (
          <ArrowUpDown className="w-4 h-4 text-[#0E3A53]/30" />
        )}
      </div>
    </th>
  );

  return (
    <div className="space-y-6 fade-in-up">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ingresos
          </h1>
          <p className="text-[#0E3A53]/70">Gestiona todos los ingresos de tu propiedad</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-[#0E3A53]/60">Total Neto:</span>
            <span className="text-2xl font-bold text-[#1AA1D8]">{formatCurrency(totalMontoNetoFiltrado)}</span>
          </div>
        </div>
        
          <Button 
            onClick={() => abrirModalRecurrente()}
            className="bg-gradient-to-r from-[#FF7F6A] to-[#FF5C3A] hover:shadow-lg"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Ingreso Recurrente
          </Button>
          {ingresosRecurrentes.length > 0 && (
            <Button 
              onClick={() => setModalVerRecurrentesAbierto(true)}
              variant="outline"
              className="border-[#1AA1D8] text-[#1AA1D8] hover:bg-[#1AA1D8]/10"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Ver Programados ({ingresosRecurrentes.length})
            </Button>
          )}

        <Button 
          data-testid="add-ingreso-btn"
          onClick={() => abrirModal()}
          className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53] hover:shadow-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuevo Ingreso
        </Button>
      </div>

      {/* Filtros */}
      <div className="card-beach p-4 filtros-container">
        <div className="flex flex-col gap-4">
          {/* Primera fila: Año, Mes, Canal, Estado */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Año</Label>
              <Select 
                value={filtroAnio || '__todos__'} 
                onValueChange={(value) => {
                  setFiltroAnio(value === '__todos__' ? '' : value);
                  // Limpiar filtros de fecha al seleccionar año
                  if (value !== '__todos__') {
                    setFiltroFechaDesde('');
                    setFiltroFechaHasta('');
                  }
                }}
                disabled={!!filtroFechaDesde || !!filtroFechaHasta}
              >
                <SelectTrigger data-testid="filtro-anio-ingreso">
                  <SelectValue placeholder="Todos los años" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos</SelectItem>
                  {aniosDisponibles.map(anio => (
                    <SelectItem key={anio} value={anio.toString()}>{anio}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Mes</Label>
              <Input
                type="month"
                value={filtroMes}
                onChange={(e) => {
                  setFiltroMes(e.target.value);
                  // Limpiar filtros de fecha al seleccionar mes
                  if (e.target.value) {
                    setFiltroFechaDesde('');
                    setFiltroFechaHasta('');
                  }
                }}
                className="w-full"
                data-testid="filtro-mes-ingreso"
                disabled={!!filtroFechaDesde || !!filtroFechaHasta}
              />
            </div>
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Canal</Label>
              <Select value={filtroCanal || '__todos__'} onValueChange={(value) => setFiltroCanal(value === '__todos__' ? '' : value)}>
                <SelectTrigger data-testid="filtro-canal-ingreso">
                  <SelectValue placeholder="Todos los canales" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos los canales</SelectItem>
                  {canales.map(canal => (
                    <SelectItem key={canal.id_canal} value={canal.id_canal}>{canal.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Estado</Label>
              <Select value={filtroEstado || '__todos__'} onValueChange={(value) => setFiltroEstado(value === '__todos__' ? '' : value)}>
                <SelectTrigger data-testid="filtro-estado-ingreso">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos los estados</SelectItem>
                  {estadosIngreso.map(estado => (
                    <SelectItem key={estado.id_estado} value={estado.nombre}>{estado.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Segunda fila: Rango de fechas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 border-t border-[#8FE0FF]/30 pt-4">
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Fecha Desde</Label>
              <Input
                type="date"
                value={filtroFechaDesde}
                onChange={(e) => {
                  setFiltroFechaDesde(e.target.value);
                  // Limpiar año y mes al usar rango de fechas
                  if (e.target.value) {
                    setFiltroAnio('');
                    setFiltroMes('');
                  }
                }}
                className="w-full"
                data-testid="filtro-fecha-desde"
              />
            </div>
            <div className="w-full">
              <Label className="text-[#0E3A53] mb-1 block text-sm">Fecha Hasta</Label>
              <Input
                type="date"
                value={filtroFechaHasta}
                onChange={(e) => {
                  setFiltroFechaHasta(e.target.value);
                  // Limpiar año y mes al usar rango de fechas
                  if (e.target.value) {
                    setFiltroAnio('');
                    setFiltroMes('');
                  }
                }}
                className="w-full"
                data-testid="filtro-fecha-hasta"
                min={filtroFechaDesde}
              />
            </div>
            <div className="hidden md:block"></div>
            <div className="col-span-2 md:col-span-1 flex items-end">
              <Button 
                variant="outline" 
                onClick={limpiarFiltros}
                className="w-full text-[#0E3A53] border-[#0E3A53]/30 hover:bg-[#0E3A53]/10"
                data-testid="limpiar-filtros-ingreso"
              >
                Limpiar filtros
              </Button>
            </div>
          </div>
        </div>
        {(filtroMes || filtroCanal || filtroEstado || filtroAnio || filtroFechaDesde || filtroFechaHasta) && (
          <p className="text-sm text-[#0E3A53]/70 mt-3">
            Mostrando {ingresosFiltrados.length} de {ingresos.length} ingresos
            {(filtroFechaDesde || filtroFechaHasta) && (
              <span className="block md:inline md:ml-2 text-[#1AA1D8]">
                ({filtroFechaDesde && `desde ${new Date(filtroFechaDesde + 'T12:00:00').toLocaleDateString('es-CL')}`}
                {filtroFechaDesde && filtroFechaHasta && ' '}
                {filtroFechaHasta && `hasta ${new Date(filtroFechaHasta + 'T12:00:00').toLocaleDateString('es-CL')}`})
              </span>
            )}
          </p>
        )}
      </div>

      <div className="card-beach">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-[#8FE0FF]">
                <HeaderOrdenable columna="fecha_cobro">Fecha</HeaderOrdenable>
                <HeaderOrdenable columna="canal">Canal</HeaderOrdenable>
                <HeaderOrdenable columna="reserva" className="min-w-[200px]">Reserva</HeaderOrdenable>
                <HeaderOrdenable columna="estado">Estado</HeaderOrdenable>
                <HeaderOrdenable columna="metodo_pago" className="hidden md:table-cell">Método Pago</HeaderOrdenable>
                <HeaderOrdenable columna="comisiones" className="hidden sm:table-cell">Comisiones</HeaderOrdenable>
                <HeaderOrdenable columna="monto_neto">Monto Neto</HeaderOrdenable>
                <th className="text-right p-3 text-[#0E3A53] font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ingresosOrdenados.map((ingreso) => {
                const reserva = ingreso.reserva_id ? obtenerReserva(ingreso.reserva_id) : null;
                const canal = obtenerCanal(ingreso.canal_id);
                return (
                <tr key={ingreso.id_ingreso} className="border-b border-[#8FE0FF]/30 hover:bg-[#8FE0FF]/10 transition-colors">
                  <td className="p-3 text-[#0E3A53]">{new Date(ingreso.fecha_cobro + 'T12:00:00').toLocaleDateString('es-CL')}</td>
                  <td className="p-3">
                    <span 
                      className="px-2 py-1 rounded-full text-xs font-medium"
                      style={{ 
                        backgroundColor: canal?.color ? `${canal.color}20` : '#8FE0FF20',
                        color: canal?.color || '#0E3A53'
                      }}
                    >
                      {canal?.nombre || '-'}
                    </span>
                  </td>
                  <td className="p-3">
                    {reserva ? (
                      <button
                        onClick={() => abrirVistaReserva(ingreso.reserva_id)}
                        className="text-left group"
                        title="Ver detalle de reserva"
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1AA1D8] to-[#0E3A53] flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-medium">
                              {reserva.huesped_nombre?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0E3A53] truncate group-hover:text-[#1AA1D8] transition-colors">
                              {reserva.huesped_nombre}
                            </p>
                            <p className="text-xs text-[#0E3A53]/60">
                              {new Date(reserva.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                              {' → '}
                              {new Date(reserva.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                            </p>
                          </div>
                          <Eye className="w-4 h-4 text-[#1AA1D8] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </div>
                      </button>
                    ) : (
                      <span className="text-xs text-[#0E3A53]/40">Sin reserva</span>
                    )}
                  </td>
                  <td className="p-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          data-testid={`estado-dropdown-${ingreso.id_ingreso}`}
                          className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity"
                          style={{
                            backgroundColor: obtenerEstadoConfig(ingreso.estado)?.color 
                              ? `${obtenerEstadoConfig(ingreso.estado).color}${obtenerEstadoConfig(ingreso.estado).color_texto === '#FFFFFF' ? '' : '30'}`
                              : '#FF7F6A20',
                            color: obtenerEstadoConfig(ingreso.estado)?.color_texto || '#FF7F6A'
                          }}
                        >
                          {ingreso.estado || 'Pendiente'}
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        {estadosIngreso.map(estado => (
                          <DropdownMenuItem
                            key={estado.id_estado}
                            data-testid={`estado-option-${estado.nombre}`}
                            onClick={() => cambiarEstadoIngreso(ingreso.id_ingreso, estado.nombre)}
                            className="cursor-pointer"
                          >
                            <span 
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: estado.color }}
                            />
                            {estado.nombre}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                  <td className="p-3 text-[#0E3A53] hidden md:table-cell">{ingreso.metodo_pago}</td>
                  <td className="p-3 text-[#FF7F6A] font-medium hidden sm:table-cell">{formatCurrency(ingreso.comisiones)}</td>
                  <td className="p-3 font-semibold text-[#1AA1D8]">{formatCurrency(ingreso.monto_neto)}</td>
                  <td className="p-3">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`edit-ingreso-${ingreso.id_ingreso}`}
                        onClick={() => abrirModal(ingreso)}
                        className="p-2 hover:bg-[#1AA1D8]/20 rounded-lg transition-colors"
                      >
                        <Edit className="w-4 h-4 text-[#1AA1D8]" />
                      </button>
                      <button
                        data-testid={`delete-ingreso-${ingreso.id_ingreso}`}
                        onClick={() => confirmarEliminacion(ingreso)}
                        className="p-2 hover:bg-[#FF7F6A]/20 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-[#FF7F6A]" />
                      </button>
                    </div>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {ingresosFiltrados.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="w-24 h-24 text-[#1AA1D8]/30 mx-auto mb-4" />
            <p className="text-lg text-[#0E3A53]/70">
              {ingresos.length === 0 ? 'No hay ingresos registrados' : 'No hay ingresos que coincidan con los filtros'}
            </p>
          </div>
        )}
      </div>

      {/* Modal Vista Previa de Reserva */}
      <Dialog open={modalReservaAbierto} onOpenChange={setModalReservaAbierto}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Detalle de Reserva
            </DialogTitle>
          </DialogHeader>
          {reservaPreview && (
            <div className="space-y-4">
              {/* Huésped */}
              <div className="flex items-center gap-3 p-3 bg-[#8FE0FF]/10 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1AA1D8] to-[#0E3A53] flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#0E3A53]">{reservaPreview.huesped_nombre}</p>
                  {reservaPreview.huesped_telefono && (
                    <p className="text-sm text-[#0E3A53]/60">{reservaPreview.huesped_telefono}</p>
                  )}
                </div>
              </div>

              {/* Fechas */}
              <div className="flex items-center gap-3 p-3 bg-[#F7E8C9]/30 rounded-xl">
                <Calendar className="w-5 h-5 text-[#0E3A53]" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-[#0E3A53]/60">Check-in</p>
                      <p className="font-medium text-[#0E3A53]">
                        {new Date(reservaPreview.fecha_check_in + 'T12:00:00').toLocaleDateString('es-CL', { 
                          day: '2-digit', month: 'short', year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="text-[#1AA1D8] font-bold">{reservaPreview.noches} noches</div>
                    <div className="text-right">
                      <p className="text-xs text-[#0E3A53]/60">Check-out</p>
                      <p className="font-medium text-[#0E3A53]">
                        {new Date(reservaPreview.fecha_check_out + 'T12:00:00').toLocaleDateString('es-CL', { 
                          day: '2-digit', month: 'short', year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Monto */}
              <div className="flex items-center gap-3 p-3 bg-[#1AA1D8]/10 rounded-xl">
                <CreditCard className="w-5 h-5 text-[#1AA1D8]" />
                <div className="flex-1">
                  <p className="text-xs text-[#0E3A53]/60">Monto Neto</p>
                  <p className="text-xl font-bold text-[#1AA1D8]">{formatCurrency(reservaPreview.monto_neto || 0)}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  reservaPreview.estado_reserva === 'Confirmada' ? 'bg-[#1AA1D8] text-white' :
                  reservaPreview.estado_reserva === 'Pendiente' ? 'bg-[#F7E8C9] text-[#0E3A53]' :
                  'bg-[#FF7F6A]/20 text-[#FF7F6A]'
                }`}>
                  {reservaPreview.estado_reserva}
                </span>
              </div>

              {/* Notas */}
              {reservaPreview.notas && (
                <div className="p-3 bg-[#0E3A53]/5 rounded-xl">
                  <p className="text-xs text-[#0E3A53]/60 mb-1">Notas</p>
                  <p className="text-sm text-[#0E3A53]">{reservaPreview.notas}</p>
                </div>
              )}

              {/* Botón para ir a reserva completa */}
              <Button
                onClick={() => {
                  setModalReservaAbierto(false);
                  navigate(`/reservas?reserva=${reservaPreview.id_reserva}`);
                }}
                className="w-full bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
              >
                Ver Reserva Completa
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alert Dialog de Confirmación */}
      <AlertDialog open={!!ingresoAEliminar} onOpenChange={() => setIngresoAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF7F6A]">
              <AlertTriangle className="w-6 h-6" />
              ¿Eliminar este ingreso?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el ingreso de:
              {ingresoAEliminar && (
                <div className="mt-2 p-3 bg-[#F7E8C9] rounded-lg">
                  <p className="font-semibold text-[#0E3A53]">{ingresoAEliminar.metodo_pago}</p>
                  <p className="text-sm text-[#0E3A53]/70">
                    {formatCurrency(ingresoAEliminar.monto_neto)} - {new Date(ingresoAEliminar.fecha_cobro).toLocaleDateString('es-CL')}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-ingreso">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-ingreso"
              onClick={eliminarIngreso}
              className="bg-[#FF7F6A] hover:bg-[#FF7F6A]/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal */}
      <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {ingresoEditando ? 'Editar Ingreso' : 'Nuevo Ingreso'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="fecha">Fecha de Cobro</Label>
              <Input
                id="fecha"
                data-testid="input-fecha-cobro"
                type="date"
                value={formData.fecha_cobro}
                onChange={(e) => setFormData({ ...formData, fecha_cobro: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="canal">Canal</Label>
              <Select value={formData.canal_id} onValueChange={(value) => setFormData({ ...formData, canal_id: value })}>
                <SelectTrigger data-testid="select-canal-ingreso">
                  <SelectValue placeholder="Seleccionar canal" />
                </SelectTrigger>
                <SelectContent>
                  {canales.map(canal => (
                    <SelectItem key={canal.id_canal} value={canal.id_canal}>{canal.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="monto">Monto Bruto</Label>
              <Input
                id="monto"
                data-testid="input-monto-bruto-ingreso"
                type="number"
                value={formData.monto_bruto || ''}
                onChange={(e) => setFormData({ ...formData, monto_bruto: Number(e.target.value) })}
                placeholder="Ingrese monto"
              />
            </div>
            <div>
              <Label htmlFor="comisiones">Comisiones</Label>
              <Input
                id="comisiones"
                data-testid="input-comisiones-ingreso"
                type="number"
                value={formData.comisiones || ''}
                onChange={(e) => setFormData({ ...formData, comisiones: Number(e.target.value) })}
                placeholder="Ingrese comisión"
              />
            </div>
            <div>
              <Label htmlFor="metodo">Método de Pago</Label>
              <Select value={formData.metodo_pago} onValueChange={(value) => setFormData({ ...formData, metodo_pago: value })}>
                <SelectTrigger data-testid="select-metodo-pago">
                  <SelectValue placeholder="Seleccionar método" />
                </SelectTrigger>
                <SelectContent>
                  {mediosPago.map(medio => (
                    <SelectItem key={medio.id_medio_pago} value={medio.nombre}>
                      {medio.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="estado">Estado</Label>
              <Select value={formData.estado} onValueChange={(value) => setFormData({ ...formData, estado: value })}>
                <SelectTrigger data-testid="select-estado-ingreso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {estadosIngreso.map(estado => (
                    <SelectItem key={estado.id_estado} value={estado.nombre}>{estado.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notas">Notas</Label>
              <Input
                id="notas"
                data-testid="input-notas-ingreso"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={() => setModalAbierto(false)} data-testid="cancel-btn-ingreso">
              Cancelar
            </Button>
            <Button 
              onClick={guardarIngreso}
              data-testid="save-ingreso-btn"
              className="bg-gradient-to-r from-[#1AA1D8] to-[#0E3A53]"
            >
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Ingreso Recurrente */}
      <Dialog open={modalRecurrenteAbierto} onOpenChange={setModalRecurrenteAbierto}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">
              {recurrenteEditando ? 'Editar Ingreso Recurrente' : 'Nuevo Ingreso Recurrente'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Canal</Label>
              <Select value={formRecurrente.canal_id} onValueChange={(v) => setFormRecurrente({...formRecurrente, canal_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar canal" /></SelectTrigger>
                <SelectContent>
                  {canales.map(c => (
                    <SelectItem key={c.id_canal} value={c.id_canal}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Día de Cobro</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={formRecurrente.dia_cobro}
                onChange={(e) => setFormRecurrente({...formRecurrente, dia_cobro: parseInt(e.target.value) || 1})}
              />
            </div>
            <div>
              <Label>Monto Bruto</Label>
              <Input
                type="number"
                value={formRecurrente.monto_bruto || ''}
                onChange={(e) => setFormRecurrente({...formRecurrente, monto_bruto: Number(e.target.value)})}
              />
            </div>
            <div>
              <Label>Comisiones</Label>
              <Input
                type="number"
                value={formRecurrente.comisiones || ''}
                onChange={(e) => setFormRecurrente({...formRecurrente, comisiones: Number(e.target.value)})}
              />
            </div>
            <div>
              <Label>Frecuencia (Meses)</Label>
              <Input
                type="number"
                min="1"
                value={formRecurrente.frecuencia_meses}
                onChange={(e) => setFormRecurrente({...formRecurrente, frecuencia_meses: parseInt(e.target.value) || 1})}
              />
            </div>
            <div>
              <Label>Repeticiones</Label>
              <Input
                type="number"
                min="1"
                value={formRecurrente.repeticiones}
                onChange={(e) => setFormRecurrente({...formRecurrente, repeticiones: parseInt(e.target.value) || 1})}
              />
            </div>
            <div>
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={formRecurrente.fecha_inicio}
                onChange={(e) => setFormRecurrente({...formRecurrente, fecha_inicio: e.target.value})}
              />
            </div>
            <div>
              <Label>Método de Pago</Label>
              <Select value={formRecurrente.metodo_pago} onValueChange={(v) => setFormRecurrente({...formRecurrente, metodo_pago: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  {mediosPago.map(medio => (
                    <SelectItem key={medio.id_medio_pago} value={medio.nombre}>
                      {medio.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Notas</Label>
              <Input
                value={formRecurrente.notas}
                onChange={(e) => setFormRecurrente({...formRecurrente, notas: e.target.value})}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t mt-4">
            <Button variant="outline" onClick={cerrarModalRecurrente}>Cancelar</Button>
            <Button onClick={guardarIngresoRecurrente} className="bg-gradient-to-r from-[#FF7F6A] to-[#FF5C3A]">
              {recurrenteEditando ? 'Guardar Cambios' : 'Crear Recurrente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Ver Programados */}
      <Dialog open={modalVerRecurrentesAbierto} onOpenChange={setModalVerRecurrentesAbierto}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0E3A53]">Ingresos Recurrentes Programados</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="activos" className="w-full mt-4">
            <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#8FE0FF]/10">
              <TabsTrigger value="activos" className="data-[state=active]:bg-[#1AA1D8] data-[state=active]:text-white">
                Activos ({ingresosRecurrentes.filter(g => g.estado_recurrente !== 'VENCIDO').length})
              </TabsTrigger>
              <TabsTrigger value="vencidos" className="data-[state=active]:bg-[#FF7F6A] data-[state=active]:text-white">
                Vencidos ({ingresosRecurrentes.filter(g => g.estado_recurrente === 'VENCIDO').length})
              </TabsTrigger>
            </TabsList>
            
            {['activos', 'vencidos'].map(tab => (
              <TabsContent key={tab} value={tab} className="max-h-[55vh] overflow-y-auto pr-2">
                {(() => {
                  const estadoFiltro = tab === 'activos' ? 'ACTIVO' : 'VENCIDO';
                  const filtrados = ingresosRecurrentes.filter(gr => 
                    (estadoFiltro === 'ACTIVO' ? gr.estado_recurrente !== 'VENCIDO' : gr.estado_recurrente === 'VENCIDO')
                  );

                  if (filtrados.length === 0) {
                    return (
                      <div className="text-center py-8 text-[#0E3A53]/60">
                        <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No hay ingresos recurrentes {tab}</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-3">
                      {filtrados.map(gr => {
                        const canal = obtenerCanal(gr.canal_id);
                        return (
                          <div key={gr.id_ingreso_recurrente} className="p-4 bg-white border border-[#8FE0FF]/30 rounded-xl hover:shadow-md transition-shadow">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-bold text-[#0E3A53]">{canal?.nombre || 'Sin canal'}</h4>
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#1AA1D8]/10 text-[#1AA1D8]">
                                    DÍA {gr.dia_cobro}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#0E3A53]/70">
                                  <span className="flex items-center gap-1">
                                    <ArrowUpDown className="w-3 h-3" />
                                    Cada {gr.frecuencia_meses} {gr.frecuencia_meses === 1 ? 'mes' : 'meses'}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {gr.repeticiones_generadas} de {gr.repeticiones} generados
                                  </span>
                                  {gr.fecha_inicio && (
                                    <span className="text-xs">
                                      Inicio: {new Date(gr.fecha_inicio + 'T12:00:00').toLocaleDateString('es-CL')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 w-full md:w-auto justify-between md:justify-end">
                                <div className="text-right">
                                  <p className="text-xs text-[#0E3A53]/60 mb-0.5">Monto Neto</p>
                                  <p className="font-bold text-[#1AA1D8]">{formatCurrency(gr.monto_bruto - gr.comisiones)}</p>
                                </div>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => verDetalleRecurrente(gr)}
                                    className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                                    title="Ver detalle de ingresos"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => abrirModalRecurrente(gr)}
                                    className="p-2 text-[#1AA1D8] hover:bg-[#1AA1D8]/10 rounded-lg transition-colors"
                                    title="Editar recurrente"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setRecurrenteAEliminar(gr)}
                                    className="p-2 text-[#FF7F6A] hover:bg-[#FF7F6A]/10 rounded-lg transition-colors"
                                    title="Eliminar recurrente"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </TabsContent>
            ))}
          </Tabs>
        </DialogContent>
      </Dialog>
      
      {/* Confirmar Eliminar Recurrente */}
      <AlertDialog open={!!recurrenteAEliminar} onOpenChange={() => setRecurrenteAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FF7F6A]">¿Eliminar Ingreso Recurrente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la programación automática.
              <div className="mt-4 flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="eliminar_pendientes" 
                  checked={eliminarPendientes}
                  onChange={(e) => setEliminarPendientes(e.target.checked)}
                />
                <label htmlFor="eliminar_pendientes" className="text-sm">Eliminar también los ingresos PENDIENTES ya generados</label>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminarIngresoRecurrente} className="bg-[#FF7F6A] hover:bg-[#FF7F6A]/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <Dialog open={modalDetalleRecurrente} onOpenChange={setModalDetalleRecurrente}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0E3A53]">
              Detalle de Ingreso Recurrente
            </DialogTitle>
          </DialogHeader>
          
          {cargandoDetalle ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-[#1AA1D8]" />
              <p className="mt-2 text-[#0E3A53]/60">Cargando...</p>
            </div>
          ) : (
            <>
              {resumenRecurrente && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{resumenRecurrente.total || 0}</p>
                    <p className="text-xs text-blue-600/70">Total Ingresos</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{resumenRecurrente.pagados || 0}</p>
                    <p className="text-xs text-green-600/70">Pagados</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-orange-600">{resumenRecurrente.pendientes || 0}</p>
                    <p className="text-xs text-orange-600/70">Pendientes</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg text-center">
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(resumenRecurrente.monto_total || 0)}</p>
                    <p className="text-xs text-purple-600/70">Monto Total</p>
                  </div>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded-lg mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div>
                    <span className="text-[#0E3A53]/60">Día de cobro:</span>
                    <span className="ml-1 font-medium">{recurrenteDetalle?.dia_cobro}</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Frecuencia:</span>
                    <span className="ml-1 font-medium">Cada {recurrenteDetalle?.frecuencia_meses} mes(es)</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Repeticiones:</span>
                    <span className="ml-1 font-medium">{recurrenteDetalle?.repeticiones}</span>
                  </div>
                  <div>
                    <span className="text-[#0E3A53]/60">Monto bruto:</span>
                    <span className="ml-1 font-medium text-[#1AA1D8]">{formatCurrency(recurrenteDetalle?.monto_bruto)}</span>
                  </div>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                {ingresosDelRecurrente.length === 0 ? (
                  <div className="p-4 text-center">
                    <p className="text-[#0E3A53]/60 mb-3">No hay ingresos vinculados a este ingreso recurrente</p>
                    <Button 
                      onClick={vincularIngresosExistentes}
                      variant="outline"
                      className="text-purple-600 border-purple-200"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Vincular ingresos existentes
                    </Button>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-b p-3 flex justify-between items-center">
                    <p className="text-sm text-[#0E3A53]/70 font-medium">Historial de ingresos</p>
                    <Button 
                      onClick={vincularIngresosExistentes}
                      variant="outline"
                      size="sm"
                      className="text-purple-600 border-purple-200 bg-white"
                      title="Buscar y vincular ingresos que hayan perdido su enlace"
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" />
                      Vincular ingresos
                    </Button>
                  </div>
                )}
                {ingresosDelRecurrente.length > 0 && (
                  <table className="w-full text-sm">
                    <thead className="bg-[#0E3A53] text-white">
                      <tr>
                         <th className="p-2 text-left">Fecha</th>
                         <th className="p-2 text-right">Monto Neto</th>
                         <th className="p-2 text-center">Estado</th>
                         <th className="p-2 text-center w-12">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ingresosDelRecurrente.map((ing, idx) => (
                        <tr key={ing.id_ingreso} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2">{new Date(ing.fecha_cobro + 'T12:00:00').toLocaleDateString('es-CL')}</td>
                          <td className="p-2 text-right font-medium text-[#1AA1D8]">{formatCurrency(ing.monto_neto)}</td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => cambiarEstadoIngresoDetalle(ing)}
                              className={`px-2 py-1 rounded-full text-[10px] font-bold cursor-pointer transition-colors ${
                                ing.estado?.toUpperCase() === 'PAGADO' || ing.estado?.toUpperCase() === 'VERIFICADO'
                                  ? 'bg-[#E5F5EC] text-[#00A650] hover:bg-[#cbf0d8]'
                                  : 'bg-[#FF7F6A]/10 text-[#FF7F6A] hover:bg-[#FF7F6A]/20'
                              }`}
                              title={ing.estado?.toUpperCase() === 'PAGADO' ? 'Marcar como pendiente' : 'Marcar como pagado'}
                            >
                              {ing.estado?.toUpperCase() === 'PAGADO' ? 'Pagado' : (ing.estado?.toUpperCase() === 'VERIFICADO' ? 'Verificado' : 'Pendiente')}
                            </button>
                          </td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => eliminarIngresoDetalle(ing)}
                              className="p-1 text-[#FF7F6A] hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar ingreso"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="flex justify-between mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setModalDetalleRecurrente(false);
                    abrirModalRecurrente(recurrenteDetalle);
                  }}
                  className="text-[#1AA1D8]"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Configuración
                </Button>
                <Button variant="outline" onClick={() => setModalDetalleRecurrente(false)}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ingresos;
